import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import Menu from "@mui/material/Menu";
import Verify from "./components/Verify.js";
import Waiting from "./components/Waiting.js";
import MuiBadge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import SellIcon from "@mui/icons-material/Sell";
import ErrorIcon from "@mui/icons-material/Error";
import AdjustIcon from "@mui/icons-material/Adjust";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import EditNoteIcon from "@mui/icons-material/EditNote";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SummarizeIcon from "@mui/icons-material/Summarize";
import FlagCircleIcon from "@mui/icons-material/FlagCircle";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CircularProgress from "@mui/material/CircularProgress";
import ChangeTargetOutcome from "../../../../../../components/Modals/LeadActions/Components/ChangeTargetOutcome.js";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { setTeamLeadDsiableScan } from "../../../../../../slices/teamActiveLeadsSlice";
import { useDispatch, useSelector } from "react-redux";
import { setMemberLeadDsiableScan } from "../../../../../../slices/memberAssignmentsSlice";
import { setMembersArrayDsiableScan } from "../../../../../../slices/membersDataArraySlice";
import { useRecordSweepQueryMutation } from "../../../../../../slices/api/apiSlice";
import { selectRunningSweep, setRunningSweep } from "../../../../../../slices/sessionDataSlice.js";

const AwaitingUpdateCard = ({
  lead,
  userId,
  userFullName,
  dashPerspective,
  handleOpenLeadActionsModal,
  cardIndex,
  teamId,
}) => {
  const dispatch = useDispatch();
  const [recordSweepQuery] = useRecordSweepQueryMutation();
  const runningSweep = useSelector(selectRunningSweep);

  const [isError, setIsError] = useState(false);
  const [changeOutcomeDetails, setChangeOutcomeDetails] = useState({});
  const [menuActive, setMenuActive] = useState(false);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [outcomeMenuAnchorEl, setOutcomeMenuAnchorEl] = useState(null);

  const mortgage = lead.belongsToMortgage;
  const openActionsMenu = Boolean(actionMenuAnchorEl);
  const openOutcomeMenu = Boolean(outcomeMenuAnchorEl);

  const handleOpenActions = (event) => {
    setActionMenuAnchorEl(event.currentTarget);
    setMenuActive(true);
  };

  const handleCloseActions = () => {
    setActionMenuAnchorEl(null);
    setMenuActive(false);
    setOutcomeMenuAnchorEl(null);
  };

  const handleOpenOutcomeMenu = (event, leadId, timeline, oldOutcome) => {
    const newOutcome = oldOutcome === "renegotiation" ? "refinance" : "renegotiation";

    setChangeOutcomeDetails({
      leadId: leadId,
      timeline: timeline,
      newOutcome: newOutcome,
    });
    setOutcomeMenuAnchorEl(event.currentTarget);
  };

  const handleCloseOutcomeMenu = () => {
    setOutcomeMenuAnchorEl(null);
    setActionMenuAnchorEl(null);
    setMenuActive(false);
  };

  const openLeadModal = (destination) => {
    handleOpenLeadActionsModal(destination, lead, mortgage);
  };

  const handleRunScan = async () => {
    setIsError(false);
    dispatch(setRunningSweep(true));
    let sweepResult = await recordSweepQuery({
      teamId: teamId,
      userId: userId,
      userFullName: userFullName,
      mortgageId: mortgage._id,
      querySettings: {
        sweepType: "Check All Discrepancies",
        sweepSize: "1",
        existingScans: "Any",
        createLead: "true",
        recordNewMonthlyStats: "false",
        recordPerformance: "true",
        notifyAssignees: "true",
        matchPublicRecordsAll: "false",
        updateTeamStats: "false",
        updateLastQuery: "false",
        initialMatch: "false",
        skipMissingVendorRejections: false,
        skipNewVendorRejections: false,
      },
      newlyGeneratedCoreLogicAccessToken: null,
      isSingleScan: true,
    });
    if (sweepResult.error) {
      setIsError(true);
    } else if (!sweepResult.data.data.publicRecordsUpdated) {
      dispatch(setTeamLeadDsiableScan({ leadId: lead._id }));
      dispatch(setMemberLeadDsiableScan({ leadId: lead._id }));
      dispatch(setMembersArrayDsiableScan({ assigneeIds: lead.assigneeIds, leadId: lead._id }));
    }
    dispatch(setRunningSweep(false));
  };

  return (
    <>
      <RankIndex>{cardIndex + 1}</RankIndex>

      <SideRail>
        <VerticalActionButton
          type="button"
          className={getTierClassName(lead.tier)}
          onClick={() => openLeadModal("compareRecords")}
        >
          <RotatedButtonLabel>
            {getRailLabel(lead, mortgage)}
            <OpenInNewIcon />
          </RotatedButtonLabel>
        </VerticalActionButton>

        <VerticalActionButton
          type="button"
          className={menuActive ? "active actions" : "actions"}
          onClick={handleOpenActions}
        >
          <RotatedButtonLabel>
            Actions
            {lead.userNotifications > 0 ? (
              <ActionNotificationBadge
                badgeContent={lead.userNotifications}
                color="error"
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <KeyboardArrowDownIcon />
              </ActionNotificationBadge>
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </RotatedButtonLabel>
        </VerticalActionButton>

        <StyledMenu
          anchorEl={actionMenuAnchorEl}
          open={openActionsMenu}
          onClose={handleCloseActions}
        >
          {mortgage.reports.length === 0 ? (
            <MenuRow
              onClick={handleCloseActions}
              disableRipple
              className="border topRadius disabled"
            >
              <MenuAction className="disabled">
                <SummarizeIcon />
                <span>Reports ({mortgage.reports.length})</span>
              </MenuAction>
            </MenuRow>
          ) : (
            <MenuRow onClick={handleCloseActions} disableRipple className="border topRadius">
              <MenuAction onClick={() => openLeadModal("reports")}>
                <SummarizeIcon />
                <span>Reports ({mortgage.reports.length})</span>
              </MenuAction>
            </MenuRow>
          )}
          <MenuRow onClick={handleCloseActions} disableRipple className="border">
            <MenuAction onClick={() => openLeadModal("notes")}>
              <EditNoteIcon />
              <span>Notes ({mortgage.mortgageNotes.length})</span>
            </MenuAction>
          </MenuRow>
          <MenuRow onClick={handleCloseActions} disableRipple className="border">
            <MenuAction onClick={() => openLeadModal("compareRecords")}>
              <FileCopyIcon />
              <span>Compare Records</span>
            </MenuAction>
          </MenuRow>
          <MenuRow onClick={handleCloseActions} disableRipple className="border">
            <MenuAction onClick={() => openLeadModal("propertyDetails")}>
              <HomeWorkIcon />
              <span>Property Details</span>
            </MenuAction>
          </MenuRow>
          {!mortgage.financialsPresent && (
            <MenuRow onClick={handleCloseActions} disableRipple className="border attention">
              <MenuAction onClick={() => openLeadModal("provideFinancialsCard")}>
                <AttachMoneyIcon />
                <span>Provide Financials</span>
              </MenuAction>
              <MenuTodo>ToDo</MenuTodo>
            </MenuRow>
          )}
          <MenuRow
            onClick={(event) =>
              handleOpenOutcomeMenu(event, lead._id, lead.timeline, lead.targetOutcome)
            }
            disableRipple
            className="border"
          >
            <MenuAction>
              <FlagCircleIcon />
              <span>
                {lead.targetOutcome === "renegotiation"
                  ? "Switch to Refinance"
                  : "Switch to Renegotiation"}
              </span>
            </MenuAction>
          </MenuRow>
          <MenuRow onClick={handleCloseActions} disableRipple className="border">
            <MenuAction onClick={() => openLeadModal("editTargets")}>
              <AdjustIcon />
              <span>Change Targets</span>
            </MenuAction>
          </MenuRow>
          <MenuRow onClick={handleCloseActions} disableRipple className="border">
            <MenuAction onClick={() => openLeadModal("editTags")}>
              <SellIcon />
              <span>Manage Tags</span>
            </MenuAction>
          </MenuRow>
          <MenuRow onClick={handleCloseActions} disableRipple className="border">
            <MenuAction onClick={() => openLeadModal("addAssignees")}>
              <PersonAddIcon />
              <span>Manage Assignees</span>
            </MenuAction>
          </MenuRow>
          <MenuRow onClick={handleCloseActions} className="button buttonTop" disableRipple>
            <MenuCommand className="advance" onClick={() => openLeadModal("confirmUpdate")}>
              Confirm Updates
            </MenuCommand>
          </MenuRow>
          <MenuRow onClick={handleCloseActions} className="button bottomRadius" disableRipple>
            <MenuCommand className="dismiss" onClick={() => openLeadModal("dismissLead")}>
              Dismiss
            </MenuCommand>
          </MenuRow>
        </StyledMenu>

        <StyledMenu
          anchorEl={outcomeMenuAnchorEl}
          open={openOutcomeMenu}
          onClose={handleCloseOutcomeMenu}
        >
          <ChangeTargetOutcome
            userId={userId}
            userFullName={userFullName}
            changeOutcomeDetails={changeOutcomeDetails}
            dashPerspective={dashPerspective}
          />
        </StyledMenu>
      </SideRail>

      <DecisionContent>
        <MainGrid>
          <WorkflowPanel>
            {runningSweep ? (
              <ScanState>
                <CircularProgress size={24} />
                <strong>Running public record scan</strong>
              </ScanState>
            ) : isError ? (
              <ScanState className="error">
                <ErrorIcon />
                <strong>Something went wrong.</strong>
                <span>If the problem continues, contact solutions@lancastersweep.com</span>
              </ScanState>
            ) : mortgage.rejectedDiscrepancies + mortgage.activeDiscrepancies !== 0 ? (
              <Waiting
                lead={lead}
                handleRunScan={handleRunScan}
                handleOpenLeadActionsModal={handleOpenLeadActionsModal}
              />
            ) : (
              <Verify lead={lead} handleOpenLeadActionsModal={handleOpenLeadActionsModal} />
            )}
          </WorkflowPanel>

          <SectionPanel className="mortgage">
            <SectionTitle>Mortgage Info</SectionTitle>
            <DetailList>
              <DetailRow
                label="Address"
                value={
                  mortgage.streetAddress.length > 0 ? (
                    <>
                      <span>{mortgage.streetAddress}</span>
                      <span>
                        {mortgage.recordDetails.address.City.currentValue},{" "}
                        {mortgage.recordDetails.address.StateOrProvince.currentValue}{" "}
                        {mortgage.recordDetails.address.PostalCode.currentValue}
                      </span>
                    </>
                  ) : (
                    "-N/A-"
                  )
                }
                muted={mortgage.streetAddress.length === 0}
              />
              <DetailRow
                label={getBorrowerLabel(mortgage)}
                value={getBorrowerValue(mortgage)}
                muted={!mortgage.owner1 && !mortgage.owner2}
              />
              <DetailRow label={getTermLabel(mortgage)} value={getTermValue(mortgage)} />
              <DetailRow
                label="Remaining"
                value={mortgage.remainingTerm.length > 0 ? mortgage.remainingTerm : "-N/A-"}
                muted={mortgage.remainingTerm.length === 0}
              />
            </DetailList>
          </SectionPanel>

          <SectionPanel className="financials">
            <SectionTitle>Financials</SectionTitle>
            <DetailList>
              {mortgage.originalLoanAmount > 0 && (
                <DetailRow label="Loan Amount" value={formatCurrency(mortgage.originalLoanAmount)} />
              )}
              {mortgage.principalRemaining > 0 && (
                <DetailRow
                  label="Principal Remaining"
                  value={formatCurrency(mortgage.principalRemaining)}
                />
              )}
              {mortgage.originalInterestRate > 0 && (
                <DetailRow
                  label="Original Interest Rate"
                  value={`${mortgage.originalInterestRate}%`}
                />
              )}
              {mortgage.originalInterestDue > 0 && (
                <DetailRow
                  label="Original Interest Due"
                  value={formatCurrency(mortgage.originalInterestDue)}
                />
              )}
              {mortgage.interestRemaining > 0 && (
                <DetailRow
                  label="Current Interest Due"
                  value={formatCurrency(mortgage.interestRemaining)}
                />
              )}
            </DetailList>

            {!mortgage.financialsPresent && (
              <FinancialCta type="button" onClick={() => openLeadModal("provideFinancialsCard")}>
                <AddCircleOutlineIcon />
                Provide Original Terms
              </FinancialCta>
            )}
          </SectionPanel>
        </MainGrid>

        <TagArea>
          <TagGroup>
            <TagLabel>mortgage tags:</TagLabel>
            {mortgage.tags.length === 0 ? (
              <EmptyTags>
                <span>no mortgage tags</span>
                <AddTagButton type="button" onClick={() => openLeadModal("editTags")}>
                  <AddCircleOutlineIcon />
                </AddTagButton>
              </EmptyTags>
            ) : (
              <TagList>
                {mortgage.tags.map((tag) =>
                  tag.apiMapping === "loanType" || tag.apiMapping === "propertyType" ? (
                    <TagButton
                      key={tag.tagId}
                      type="button"
                      className="mortgageTag"
                      onClick={() => openLeadModal("editTags")}
                    >
                      <strong>{tag.label}</strong> - {tag.discrepancyFields[0].toUpperCase()}
                    </TagButton>
                  ) : null
                )}
                {mortgage.tags.map((tag) =>
                  tag.apiMapping !== "loanType" && tag.apiMapping !== "propertyType" ? (
                    <TagButton
                      key={tag.tagId}
                      type="button"
                      className="mortgageTag"
                      onClick={() => openLeadModal("editTags")}
                    >
                      {tag.label}
                    </TagButton>
                  ) : null
                )}
              </TagList>
            )}
          </TagGroup>

          <TagGroup>
            <TagLabel>investigation tags:</TagLabel>
            {lead.tags.length === 0 ? (
              <EmptyTags>
                <span>no lead tags</span>
                <AddTagButton type="button" onClick={() => openLeadModal("editTags")}>
                  <AddCircleOutlineIcon />
                </AddTagButton>
              </EmptyTags>
            ) : (
              <TagList>
                {lead.tags.map((tag) => (
                  <TagButton key={tag.tagId} type="button" onClick={() => openLeadModal("editTags")}>
                    {tag.label}
                  </TagButton>
                ))}
              </TagList>
            )}
          </TagGroup>
        </TagArea>
      </DecisionContent>
    </>
  );
};

const DetailRow = ({ label, value, muted = false }) => (
  <DataRow>
    <DataLabel>{label}:</DataLabel>
    <DataValue className={muted ? "muted" : ""}>{value}</DataValue>
  </DataRow>
);

const getTierClassName = (tier) => {
  if (tier === 0 || tier === 5) return "manual";
  if (tier === 1) return "tierOne";
  if (tier === 2) return "tierTwo";
  return "tierThree";
};

const getRailLabel = (lead, mortgage) => {
  if (lead.tier !== 0 && lead.tier !== 1 && lead.tier !== 2 && lead.tier !== 3) {
    return "Internal Records Updated";
  }

  if (lead.tier === 0 || lead.tier === 5) {
    return "Manual Investigation";
  }

  return (
    <>
      {mortgage.activeDiscrepancies > 0 && <InlineBadge>{mortgage.activeDiscrepancies}</InlineBadge>}
      Tier {lead.tier}
    </>
  );
};

const getBorrowerLabel = (mortgage) => {
  if (mortgage.owner1 && mortgage.owner2) return "Borrowers";
  if (mortgage.owner1 || mortgage.owner2) return "Borrower";
  return "Borrowers";
};

const getBorrowerValue = (mortgage) => {
  if (mortgage.owner1.length > 0 && mortgage.owner2.length > 0) {
    return (
      <StackedValue>
        <span>{mortgage.owner1}</span>
        <span>{mortgage.owner2}</span>
      </StackedValue>
    );
  }

  if (mortgage.owner1.length > 0) return mortgage.owner1;
  if (mortgage.owner2.length > 0) return mortgage.owner2;
  return "-N/A-";
};

const getTermLabel = (mortgage) => {
  if (mortgage.originationDateLabel.length > 0 && mortgage.endDateLabel.length === 0) {
    return "Originated";
  }

  if (mortgage.endDateLabel.length > 0 && !mortgage.originationDateLabel.length > 0) {
    return "Terminates";
  }

  return "Term";
};

const getTermValue = (mortgage) => {
  if (mortgage.originationDateLabel.length > 0 && mortgage.endDateLabel.length > 0) {
    return (
      <InlinePair>
        <span>{mortgage.originationDateLabel}</span>
        <small>to</small>
        <span>{mortgage.endDateLabel}</span>
      </InlinePair>
    );
  }

  if (mortgage.originationDateLabel.length > 0) return mortgage.originationDateLabel;
  if (mortgage.endDateLabel.length > 0) return mortgage.endDateLabel;
  return "-N/A-";
};

const formatCurrency = (value) =>
  `$${value.toLocaleString("en-US", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })}`;

const RankIndex = styled("div")(({ theme }) => ({
  width: "2.2rem",
  minWidth: "2.2rem",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.common.grayText,
  fontSize: "1rem",
  fontWeight: 900,
  background: "rgba(248,250,252,0.84)",
  borderRight: `0.1rem solid ${theme.palette.common.grayBorder}`,
}));

const SideRail = styled("div")(({ theme }) => ({
  width: "5.4rem",
  minWidth: "5.4rem",
  height: "100%",
  display: "flex",
  alignItems: "stretch",
  justifyContent: "center",
  gap: "0.35rem",
  padding: "0.65rem 0.35rem",
  borderRight: `0.1rem solid ${theme.palette.common.grayBorder}`,
  background: "rgba(248,250,252,0.82)",
}));

const VerticalActionButton = styled("button")({
  width: "2.1rem",
  minWidth: "2.1rem",
  height: "100%",
  minHeight: "12rem",
  border: 0,
  borderRadius: "0.55rem",
  cursor: "pointer",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 160ms ease, box-shadow 160ms ease, background 160ms ease",

  "&:hover": {
    transform: "translateY(-0.1rem)",
    boxShadow: "0 0.5rem 1.1rem rgba(15, 23, 42, 0.13)",
  },

  "& svg": {
    width: "1.15rem",
    height: "1.15rem",
  },

  "&.manual": {
    background: "#475569",
  },

  "&.tierOne": {
    background: "#dc2626",
  },

  "&.tierTwo": {
    background: "#7c3aed",
  },

  "&.tierThree": {
    background: "#0f766e",
  },

  "&.actions": {
    background: "#334155",
  },

  "&.active": {
    background: "#1e40af",
    boxShadow: "0 0 0 0.16rem rgba(37,99,235,0.16)",
  },
});

const RotatedButtonLabel = styled("span")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.3rem",
  transform: "rotate(-90deg)",
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  fontSize: "0.82rem",
  fontWeight: 950,
});

const InlineBadge = styled("span")({
  minWidth: "1.7rem",
  height: "1.7rem",
  padding: "0 0.45rem",
  borderRadius: "999px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#0f172a",
  background: "#fff",
  boxShadow: "inset 0 0 0 0.1rem rgba(15,23,42,0.14)",
  fontSize: "0.78rem",
  fontWeight: 950,
});

const ActionNotificationBadge = styled(MuiBadge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.common.error,
    fontWeight: 800,
    fontSize: "1rem",
    height: "1.4rem",
    padding: "0 0.5rem",
    borderRadius: "1rem",
    minWidth: "1.2rem",
  },
}));

const DecisionContent = styled("section")({
  minWidth: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "0.8rem",
  padding: "0.9rem 1rem 0.85rem 0.95rem",
  background: "rgba(255,255,255,0.8)",
});

const MainGrid = styled("div")({
  minWidth: 0,
  display: "grid",
  gridTemplateColumns: "31rem minmax(24rem, 1fr) minmax(18rem, 0.8fr)",
  gap: "0.75rem",
  alignItems: "stretch",
});

const WorkflowPanel = styled("section")(({ theme }) => ({
  minWidth: "31rem",
  display: "flex",
  alignItems: "stretch",
  justifyContent: "center",
  border: `0.1rem solid ${theme.palette.common.grayBorder}`,
  borderLeft: "0.24rem solid #0f766e",
  borderRadius: "0.7rem",
  background: "#fff",
  overflow: "hidden",
}));

const ScanState = styled("div")(({ theme }) => ({
  minWidth: "31rem",
  minHeight: "13rem",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.6rem",
  color: "#0f172a",
  fontSize: "0.9rem",
  fontWeight: 850,

  "& .MuiCircularProgress-root": {
    color: theme.palette.primary.main,
  },

  "&.error": {
    color: theme.palette.common.redDark,
  },

  "&.error svg": {
    width: "1.5rem",
    height: "1.5rem",
  },

  "& span": {
    maxWidth: "22rem",
    color: theme.palette.common.redDark,
    textAlign: "center",
    fontSize: "0.78rem",
    fontWeight: 750,
  },
}));

const SectionPanel = styled("section")(({ theme }) => ({
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  border: `0.1rem solid ${theme.palette.common.grayBorder}`,
  borderLeft: "0.24rem solid #334155",
  borderRadius: "0.7rem",
  background: "#fff",
  overflow: "hidden",

  "&.mortgage": {
    borderLeftColor: "#2563eb",
  },

  "&.financials": {
    borderLeftColor: "#0f766e",
  },
}));

const SectionTitle = styled("h3")(({ theme }) => ({
  margin: 0,
  minHeight: "2.6rem",
  display: "flex",
  alignItems: "center",
  padding: "0 0.85rem",
  color: "#0f172a",
  background: "rgba(248,250,252,0.95)",
  borderBottom: `0.1rem solid ${theme.palette.common.grayBorder}`,
  fontSize: "0.86rem",
  fontWeight: 950,
  lineHeight: 1.15,
  textTransform: "uppercase",
}));

const DetailList = styled("div")({
  display: "grid",
  gap: "0.36rem",
  padding: "0.82rem 0.85rem",
});

const DataRow = styled("div")({
  minWidth: 0,
  display: "grid",
  gridTemplateColumns: "8.2rem minmax(0, 1fr)",
  gap: "0.55rem",
  alignItems: "start",
});

const DataLabel = styled("span")(({ theme }) => ({
  color: theme.palette.common.grayText,
  fontSize: "0.76rem",
  fontWeight: 850,
  lineHeight: 1.2,
  textAlign: "right",
}));

const DataValue = styled("span")({
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  color: "#0f172a",
  fontSize: "0.82rem",
  fontWeight: 900,
  lineHeight: 1.2,

  "&.muted": {
    color: "#64748b",
    fontWeight: 750,
  },

  "& span": {
    minWidth: 0,
    color: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
  },
});

const InlinePair = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "0.28rem",
  minWidth: 0,
  maxWidth: "100%",
  color: "inherit",
  fontSize: "inherit",
  fontWeight: "inherit",
  lineHeight: "inherit",

  "& span": {
    minWidth: 0,
    color: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
  },

  "& small": {
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "center",
    color: "#64748b",
    fontSize: "0.76rem",
    fontWeight: 750,
    lineHeight: 1,
  },
});

const StackedValue = styled("span")({
  minWidth: 0,
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "0.12rem",
  color: "inherit",
  fontSize: "inherit",
  fontWeight: "inherit",
  lineHeight: "inherit",
});

const FinancialCta = styled("button")({
  minHeight: "3.2rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.45rem",
  margin: "0.45rem auto 0",
  padding: "0 0.9rem",
  border: "0.1rem solid rgba(234,88,12,0.28)",
  borderRadius: "0.65rem",
  background: "#fff7ed",
  color: "#c2410c",
  cursor: "pointer",
  fontSize: "0.88rem",
  fontWeight: 950,

  "& svg": {
    width: "1.25rem",
    height: "1.25rem",
  },
});

const TagArea = styled("div")(({ theme }) => ({
  display: "grid",
  gap: "0.42rem",
  paddingTop: "0.4rem",
  borderTop: `0.1rem solid ${theme.palette.common.grayBorder}`,
}));

const TagGroup = styled("div")({
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
});

const TagLabel = styled("span")(({ theme }) => ({
  color: theme.palette.common.grayText,
  fontSize: "0.78rem",
  fontWeight: 900,
  whiteSpace: "nowrap",
}));

const TagList = styled("div")({
  minWidth: 0,
  display: "flex",
  flexWrap: "wrap",
  gap: "0.35rem",
});

const TagButton = styled("button")(({ theme }) => ({
  minHeight: "1.85rem",
  display: "inline-flex",
  alignItems: "center",
  gap: "0.25rem",
  padding: "0.15rem 0.55rem",
  border: `0.1rem solid ${theme.palette.common.grayBorder}`,
  borderRadius: "0.55rem",
  background: "#fff",
  color: "#334155",
  cursor: "pointer",
  fontSize: "0.78rem",
  fontWeight: 850,

  "& strong": {
    color: theme.palette.common.grayBorderDarkest,
    fontSize: "0.78rem",
    fontWeight: 900,
  },

  "&.mortgageTag": {
    background: "rgba(248,250,252,0.92)",
  },
}));

const EmptyTags = styled("div")({
  display: "inline-flex",
  alignItems: "center",
  gap: "0.35rem",
  color: "#64748b",
  fontSize: "0.78rem",
  fontWeight: 800,
});

const AddTagButton = styled("button")(({ theme }) => ({
  width: "1.6rem",
  height: "1.6rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: `0.1rem solid ${theme.palette.common.grayBorder}`,
  borderRadius: "50%",
  background: "#fff",
  color: theme.palette.primary.main,
  cursor: "pointer",

  "& svg": {
    width: "1rem",
    height: "1rem",
  },
}));

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "left",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    minWidth: "17rem",
    border: `0.1rem solid ${theme.palette.common.grayBorder}`,
    borderRadius: "0.8rem",
    background: theme.palette.common.grayBorder,
    boxShadow: "0 1rem 2rem rgba(15, 23, 42, 0.14)",

    "& .MuiMenu-list": {
      padding: "0.35rem",
    },

    "& .MuiMenuItem-root": {
      background: theme.palette.common.pageBackground,

      "&.buttonTop": {
        padding: "0.8rem 0.8rem 0.4rem",
        justifyContent: "center",
      },

      "&.border": {
        borderBottom: `0.1rem solid ${theme.palette.common.grayBorder}`,
      },

      "&.topRadius": {
        borderRadius: "0.55rem 0.55rem 0 0",
      },

      "&.bottomRadius": {
        borderRadius: "0 0 0.55rem 0.55rem",
        padding: "0.4rem 0.8rem 0.8rem",
        justifyContent: "center",
      },

      "&.disabled": {
        background: theme.palette.common.disabledGray,
      },

      "&.attention": {
        background: "#fff4e6",
      },
    },

    "& .MuiMenuItem-root:hover": {
      background: theme.palette.common.grayHover,

      "&.button": {
        background: theme.palette.common.pageBackground,
      },
    },
  },
}));

const MenuRow = styled(MenuItem)({
  minHeight: "3rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.32rem 0.45rem",

  "&.button": {
    justifyContent: "center",
  },
});

const MenuAction = styled("button")(({ theme }) => ({
  width: "100%",
  minHeight: "2.35rem",
  display: "flex",
  alignItems: "center",
  gap: "0.65rem",
  padding: "0 0.35rem",
  border: 0,
  background: "transparent",
  color: "#0f172a",
  cursor: "pointer",
  fontSize: "0.9rem",
  fontWeight: 850,
  textAlign: "left",

  "& svg": {
    width: "1.55rem",
    height: "1.55rem",
    color: theme.palette.common.grayText,
  },

  ".attention & svg": {
    color: theme.accent.caution.primary,
  },

  "&.disabled": {
    cursor: "default",
    color: theme.palette.common.grayText,
  },
}));

const MenuTodo = styled("span")(({ theme }) => ({
  marginRight: "0.35rem",
  color: theme.accent.caution.primary,
  fontSize: "0.72rem",
  fontWeight: 950,
}));

const MenuCommand = styled("button")({
  width: "100%",
  minHeight: "2.4rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: 0,
  borderRadius: "0.55rem",
  color: "#fff",
  cursor: "pointer",
  fontSize: "0.9rem",
  fontWeight: 950,

  "&.advance": {
    background: "#0f766e",
  },

  "&.dismiss": {
    background: "#dc2626",
  },
});

export default AwaitingUpdateCard;
